<script>
export default { name: 'OrderBuilderModal' };
</script>

<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import OrdersApi from 'dashboard/api/orders';
import { useVuelidate } from '@vuelidate/core';
import {
  required,
  minValue,
  numeric,
  helpers,
  url,
} from '@vuelidate/validators';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  conversationId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['close', 'update:show']);
const { t } = useI18n();

const ORDER_TYPES = ['digital-goods', 'physical-goods'];
const PAYMENT_METHODS = ['pix_dynamic_code', 'boleto', 'payment_link'];
const PIX_KEY_TYPES = ['CNPJ', 'CPF', 'EMAIL', 'PHONE', 'EVP'];

const referenceIdFormat = helpers.regex(/^[A-Za-z0-9_.-]{1,60}$/);
const atLeastOnePaymentMethod = value =>
  Array.isArray(value) && value.length > 0;

const localShow = computed({
  get: () => props.show,
  set: value => emit('update:show', value),
});

const referenceId = ref('');
const orderType = ref('digital-goods');
const selectedPaymentMethods = ref(['pix_dynamic_code']);
const bodyText = ref('');
const footerText = ref('');
const providerName = ref('');
const providerOrderId = ref('');
const providerConfigs = ref([]);
const isLoadingProviderConfigs = ref(false);
const providerConfigsError = ref(false);
const totalAmountCents = ref(0);
const includeItems = ref(false);
const tax = ref(0);
const shipping = ref(0);
const discount = ref(0);
const isSubmitting = ref(false);

const paymentSettings = ref({
  pixCode: '',
  pixMerchantName: '',
  pixKey: '',
  pixKeyType: 'CNPJ',
  boletoDigitableLine: '',
  paymentLinkUri: '',
});

const items = ref([
  {
    retailer_id: '',
    name: '',
    amount: 0,
    quantity: 1,
  },
]);

const isPaymentMethodSelected = method =>
  selectedPaymentMethods.value.includes(method);

const providerOptions = computed(() =>
  providerConfigs.value.map(config => ({
    id: config.id,
    name: config.provider_name,
    enabledMethods: config.enabled_methods || [],
    inboxId: config.inbox_id,
    oneClickEligible: config.one_click_eligible === true,
  }))
);

const hasProviderConfigs = computed(() => providerOptions.value.length > 0);

const canSetProviderOrderId = computed(
  () => hasProviderConfigs.value && Boolean(providerName.value)
);

const orderTypeLabel = type => {
  if (type === 'physical-goods') {
    return t('ORDERS.ORDER_TYPE_PHYSICAL_GOODS');
  }

  return t('ORDERS.ORDER_TYPE_DIGITAL_GOODS');
};

const paymentMethodLabel = method => {
  if (method === 'boleto') {
    return t('ORDERS.PAYMENT_TYPE_BOLETO');
  }

  if (method === 'payment_link') {
    return t('ORDERS.PAYMENT_TYPE_LINK');
  }

  return t('ORDERS.PAYMENT_TYPE_PIX');
};

const subtotal = computed(() => {
  return items.value.reduce((acc, item) => {
    return acc + (Number(item.amount) || 0) * (Number(item.quantity) || 0);
  }, 0);
});

const totalAmount = computed(() => {
  if (!includeItems.value) {
    return Number(totalAmountCents.value) || 0;
  }

  return (
    subtotal.value +
    (Number(tax.value) || 0) +
    (Number(shipping.value) || 0) -
    (Number(discount.value) || 0)
  );
});

const rules = computed(() => {
  const baseRules = {
    referenceId: { required, referenceIdFormat },
    orderType: { required },
    selectedPaymentMethods: { atLeastOnePaymentMethod },
    paymentSettings: {
      pixCode: isPaymentMethodSelected('pix_dynamic_code') ? { required } : {},
      pixMerchantName: isPaymentMethodSelected('pix_dynamic_code')
        ? { required }
        : {},
      pixKey: isPaymentMethodSelected('pix_dynamic_code') ? { required } : {},
      pixKeyType: isPaymentMethodSelected('pix_dynamic_code')
        ? { required }
        : {},
      boletoDigitableLine: isPaymentMethodSelected('boleto')
        ? { required }
        : {},
      paymentLinkUri: isPaymentMethodSelected('payment_link')
        ? { required, url }
        : {},
    },
    tax: { numeric, minValue: minValue(0) },
    shipping: { numeric, minValue: minValue(0) },
    discount: {
      numeric,
      minValue: minValue(0),
      itemizedTotalPositive: () => !includeItems.value || totalAmount.value > 0,
    },
  };

  if (includeItems.value) {
    baseRules.items = {
      $each: helpers.forEach({
        name: { required },
        amount: { required, numeric, minValue: minValue(1) },
        quantity: { required, numeric, minValue: minValue(1) },
      }),
    };
  } else {
    baseRules.totalAmountCents = { required, numeric, minValue: minValue(1) };
  }

  return baseRules;
});

const v$ = useVuelidate(rules, {
  referenceId,
  orderType,
  selectedPaymentMethods,
  paymentSettings,
  totalAmountCents,
  tax,
  shipping,
  discount,
  items,
});

const addItem = () => {
  items.value.push({ retailer_id: '', name: '', amount: 0, quantity: 1 });
};

const removeItem = index => {
  if (items.value.length > 1) {
    items.value.splice(index, 1);
  }
};

const trimmedValue = value => value?.toString().trim() || '';

const resetProviderSelection = () => {
  providerName.value = '';
  providerOrderId.value = '';
};

const fetchProviderConfigs = async () => {
  isLoadingProviderConfigs.value = true;
  providerConfigsError.value = false;

  try {
    const response = await OrdersApi.providerConfigs({
      conversationId: props.conversationId,
    });
    providerConfigs.value = response?.data?.provider_configs || [];

    const selectedProviderExists = providerOptions.value.some(
      provider => provider.name === providerName.value
    );
    if (!selectedProviderExists) resetProviderSelection();
  } catch {
    providerConfigs.value = [];
    providerConfigsError.value = true;
    resetProviderSelection();
  } finally {
    isLoadingProviderConfigs.value = false;
  }
};

const itemFieldHasError = (index, field) =>
  Boolean(v$.value.items?.$each?.$response?.$errors?.[index]?.[field]?.length);

const buildPaymentSettings = () => {
  return selectedPaymentMethods.value.map(method => {
    if (method === 'pix_dynamic_code') {
      return {
        type: 'pix_dynamic_code',
        pix_dynamic_code: {
          code: trimmedValue(paymentSettings.value.pixCode),
          merchant_name: trimmedValue(paymentSettings.value.pixMerchantName),
          key: trimmedValue(paymentSettings.value.pixKey),
          key_type: trimmedValue(paymentSettings.value.pixKeyType),
        },
      };
    }

    if (method === 'boleto') {
      return {
        type: 'boleto',
        boleto: {
          digitable_line: trimmedValue(
            paymentSettings.value.boletoDigitableLine
          ),
        },
      };
    }

    return {
      type: 'payment_link',
      payment_link: {
        uri: trimmedValue(paymentSettings.value.paymentLinkUri),
      },
    };
  });
};

const buildOrderPayload = () => {
  const payload = {
    reference_id: trimmedValue(referenceId.value),
    order_type: orderType.value,
    total_amount: totalAmount.value,
    currency: 'BRL',
    payment_type: selectedPaymentMethods.value[0],
    payment_settings: buildPaymentSettings(),
  };

  const body = trimmedValue(bodyText.value);
  const footer = trimmedValue(footerText.value);
  const provider = trimmedValue(providerName.value);
  const providerReference = trimmedValue(providerOrderId.value);

  if (body) payload.body_text = body;
  if (footer) payload.footer_text = footer;
  if (provider) payload.provider_name = provider;
  if (provider && providerReference) {
    payload.provider_order_id = providerReference;
  }

  if (includeItems.value) {
    payload.items = items.value.map(item => ({
      retailer_id: trimmedValue(item.retailer_id),
      name: trimmedValue(item.name),
      amount: Number(item.amount),
      quantity: Number(item.quantity),
    }));
    payload.tax = Number(tax.value) || 0;

    const shippingAmount = Number(shipping.value) || 0;
    const discountAmount = Number(discount.value) || 0;
    if (shippingAmount > 0) payload.shipping = shippingAmount;
    if (discountAmount > 0) payload.discount = discountAmount;
  }

  return payload;
};

const onClose = () => {
  referenceId.value = '';
  orderType.value = 'digital-goods';
  selectedPaymentMethods.value = ['pix_dynamic_code'];
  bodyText.value = '';
  footerText.value = '';
  resetProviderSelection();
  totalAmountCents.value = 0;
  includeItems.value = false;
  tax.value = 0;
  shipping.value = 0;
  discount.value = 0;
  paymentSettings.value = {
    pixCode: '',
    pixMerchantName: '',
    pixKey: '',
    pixKeyType: 'CNPJ',
    boletoDigitableLine: '',
    paymentLinkUri: '',
  };
  items.value = [
    {
      retailer_id: '',
      name: '',
      amount: 0,
      quantity: 1,
    },
  ];
  v$.value.$reset();
  emit('close');
};

watch(
  () => props.show,
  value => {
    if (value) fetchProviderConfigs();
  },
  { immediate: true }
);

const onSubmit = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    useAlert(t('ORDERS.MISSING_FIELDS_ERROR'));
    return;
  }

  const payload = buildOrderPayload();

  isSubmitting.value = true;
  try {
    await OrdersApi.create({
      conversationId: props.conversationId,
      payload,
    });
    useAlert(t('ORDERS.CREATE_SUCCESS'));
    onClose();
  } catch (error) {
    const errorMsg =
      error?.response?.data?.error || error.message || t('ORDERS.CREATE_ERROR');
    useAlert(errorMsg);
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <woot-modal v-model:show="localShow" :on-close="onClose" size="modal-big">
    <div class="flex flex-col h-full max-h-[80vh] overflow-y-auto">
      <woot-modal-header :header-title="t('ORDERS.CREATE_ORDER')" />
      <form
        class="flex flex-col w-full gap-4 px-6 py-4"
        novalidate
        @submit.prevent="onSubmit"
      >
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label>
            <span class="block mb-1 text-sm font-medium">
              {{ t('ORDERS.REFERENCE_ID') }}
            </span>
            <input
              v-model="referenceId"
              data-testid="reference-id-input"
              type="text"
              class="w-full input"
              :class="{ 'border-red-500': v$.referenceId.$error }"
              :placeholder="t('ORDERS.PLACEHOLDERS.REFERENCE_ID')"
            />
            <span v-if="v$.referenceId.$error" class="text-xs text-red-500">
              {{
                v$.referenceId.required.$invalid
                  ? t('ORDERS.REFERENCE_ID_REQUIRED')
                  : t('ORDERS.REFERENCE_ID_INVALID')
              }}
            </span>
          </label>

          <label>
            <span class="block mb-1 text-sm font-medium">
              {{ t('ORDERS.ORDER_TYPE') }}
            </span>
            <select
              v-model="orderType"
              data-testid="order-type-select"
              class="w-full input"
            >
              <option v-for="type in ORDER_TYPES" :key="type" :value="type">
                {{ orderTypeLabel(type) }}
              </option>
            </select>
          </label>
        </div>

        <fieldset>
          <legend class="mb-2 text-sm font-medium">
            {{ t('ORDERS.PAYMENT_TYPE') }}
          </legend>
          <div class="grid grid-cols-1 gap-2 md:grid-cols-3">
            <label
              v-for="method in PAYMENT_METHODS"
              :key="method"
              class="flex items-center gap-2 px-3 py-2 rounded-md border border-n-slate-3"
            >
              <input
                v-model="selectedPaymentMethods"
                :data-testid="`payment-method-${method}`"
                type="checkbox"
                :value="method"
              />
              <span class="text-sm font-medium">
                {{ paymentMethodLabel(method) }}
              </span>
            </label>
          </div>
          <span
            v-if="v$.selectedPaymentMethods.$error"
            class="text-xs text-red-500"
          >
            {{ t('ORDERS.PAYMENT_METHOD_REQUIRED') }}
          </span>
        </fieldset>

        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label>
            <span class="block mb-1 text-sm font-medium">
              {{ t('ORDERS.MESSAGE_BODY') }}
            </span>
            <input
              v-model="bodyText"
              data-testid="body-text-input"
              type="text"
              class="w-full input"
              :placeholder="t('ORDERS.PLACEHOLDERS.MESSAGE_BODY')"
            />
          </label>

          <label>
            <span class="block mb-1 text-sm font-medium">
              {{ t('ORDERS.FOOTER_TEXT') }}
            </span>
            <input
              v-model="footerText"
              data-testid="footer-text-input"
              type="text"
              class="w-full input"
              :placeholder="t('ORDERS.PLACEHOLDERS.FOOTER_TEXT')"
            />
          </label>
        </div>

        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label>
            <span class="block mb-1 text-sm font-medium">
              {{ t('ORDERS.PROVIDER_NAME') }}
            </span>
            <select
              v-model="providerName"
              data-testid="provider-name-select"
              class="w-full input"
              :disabled="isLoadingProviderConfigs || !hasProviderConfigs"
            >
              <option value="">
                {{
                  hasProviderConfigs
                    ? t('ORDERS.PROVIDER_NONE')
                    : t('ORDERS.PROVIDER_UNCONFIGURED')
                }}
              </option>
              <option
                v-for="provider in providerOptions"
                :key="provider.id"
                :value="provider.name"
              >
                {{ provider.name }}
              </option>
            </select>
            <span
              v-if="providerConfigsError"
              class="block mt-1 text-xs text-red-500"
            >
              {{ t('ORDERS.PROVIDER_CONFIG_LOAD_ERROR') }}
            </span>
          </label>

          <label>
            <span class="block mb-1 text-sm font-medium">
              {{ t('ORDERS.PROVIDER_ORDER_ID') }}
            </span>
            <input
              v-model="providerOrderId"
              data-testid="provider-order-id-input"
              type="text"
              class="w-full input"
              :disabled="!canSetProviderOrderId"
              :placeholder="t('ORDERS.PLACEHOLDERS.PROVIDER_ORDER_ID')"
            />
          </label>
        </div>

        <label v-if="!includeItems">
          <span class="block mb-1 text-sm font-medium">
            {{ t('ORDERS.TOTAL_AMOUNT_CENTS') }}
          </span>
          <input
            v-model="totalAmountCents"
            data-testid="total-amount-input"
            type="number"
            class="w-full input"
            :class="{ 'border-red-500': v$.totalAmountCents.$error }"
            min="1"
          />
        </label>

        <div
          v-if="isPaymentMethodSelected('pix_dynamic_code')"
          class="p-4 rounded-md bg-n-slate-2"
        >
          <h4 class="mb-2 font-medium">{{ t('ORDERS.PIX_SETTINGS') }}</h4>
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label class="block md:col-span-2">
              <span class="block mb-1 text-sm">{{ t('ORDERS.PIX_CODE') }}</span>
              <input
                v-model="paymentSettings.pixCode"
                data-testid="pix-code-input"
                type="text"
                class="w-full input"
                :class="{
                  'border-red-500': v$.paymentSettings.pixCode.$error,
                }"
                :placeholder="t('ORDERS.PLACEHOLDERS.PIX_CODE')"
              />
            </label>
            <label class="block">
              <span class="block mb-1 text-sm">
                {{ t('ORDERS.MERCHANT_NAME') }}
              </span>
              <input
                v-model="paymentSettings.pixMerchantName"
                data-testid="pix-merchant-name-input"
                type="text"
                class="w-full input"
                :class="{
                  'border-red-500': v$.paymentSettings.pixMerchantName.$error,
                }"
                :placeholder="t('ORDERS.PLACEHOLDERS.MERCHANT_NAME')"
              />
            </label>
            <label class="block">
              <span class="block mb-1 text-sm">
                {{ t('ORDERS.PIX_KEY') }}
              </span>
              <input
                v-model="paymentSettings.pixKey"
                data-testid="pix-key-input"
                type="text"
                class="w-full input"
                :class="{
                  'border-red-500': v$.paymentSettings.pixKey.$error,
                }"
                :placeholder="t('ORDERS.PLACEHOLDERS.PIX_KEY')"
              />
            </label>
            <label class="block">
              <span class="block mb-1 text-sm">
                {{ t('ORDERS.PIX_KEY_TYPE') }}
              </span>
              <select
                v-model="paymentSettings.pixKeyType"
                data-testid="pix-key-type-select"
                class="w-full input"
                :class="{
                  'border-red-500': v$.paymentSettings.pixKeyType.$error,
                }"
              >
                <option
                  v-for="keyType in PIX_KEY_TYPES"
                  :key="keyType"
                  :value="keyType"
                >
                  {{ keyType }}
                </option>
              </select>
            </label>
          </div>
        </div>

        <div
          v-if="isPaymentMethodSelected('boleto')"
          class="p-4 rounded-md bg-n-slate-2"
        >
          <h4 class="mb-2 font-medium">{{ t('ORDERS.BOLETO_SETTINGS') }}</h4>
          <label class="block">
            <span class="block mb-1 text-sm">
              {{ t('ORDERS.BOLETO_DIGITABLE_LINE') }}
            </span>
            <input
              v-model="paymentSettings.boletoDigitableLine"
              data-testid="boleto-line-input"
              type="text"
              class="w-full input"
              :class="{
                'border-red-500': v$.paymentSettings.boletoDigitableLine.$error,
              }"
              :placeholder="t('ORDERS.PLACEHOLDERS.BOLETO_DIGITABLE_LINE')"
            />
          </label>
        </div>

        <div
          v-if="isPaymentMethodSelected('payment_link')"
          class="p-4 rounded-md bg-n-slate-2"
        >
          <h4 class="mb-2 font-medium">
            {{ t('ORDERS.PAYMENT_LINK_SETTINGS') }}
          </h4>
          <label class="block">
            <span class="block mb-1 text-sm">
              {{ t('ORDERS.PAYMENT_URI') }}
            </span>
            <input
              v-model="paymentSettings.paymentLinkUri"
              data-testid="payment-link-uri-input"
              type="url"
              class="w-full input"
              :class="{
                'border-red-500': v$.paymentSettings.paymentLinkUri.$error,
              }"
              :placeholder="t('ORDERS.PLACEHOLDERS.PAYMENT_URI')"
            />
          </label>
        </div>

        <label class="flex items-center gap-2">
          <input
            v-model="includeItems"
            data-testid="include-items-input"
            type="checkbox"
          />
          <span class="text-sm font-medium">{{
            t('ORDERS.INCLUDE_ITEMS')
          }}</span>
        </label>

        <div v-if="includeItems">
          <h4 class="mb-2 font-medium">{{ t('ORDERS.ITEMS') }}</h4>
          <div
            v-for="(item, index) in items"
            :key="index"
            class="p-4 mb-4 rounded-md border border-n-slate-3"
          >
            <div class="flex justify-between mb-2">
              <span class="text-sm font-medium">
                {{ t('ORDERS.ITEM') }} {{ index + 1 }}
              </span>
              <button
                v-if="items.length > 1"
                type="button"
                class="text-xs text-red-500"
                @click="removeItem(index)"
              >
                {{ t('ORDERS.REMOVE') }}
              </button>
            </div>
            <div class="grid grid-cols-2 gap-4">
              <label>
                <span class="block mb-1 text-xs">{{
                  t('ORDERS.RETAILER_ID')
                }}</span>
                <input
                  v-model="item.retailer_id"
                  :data-testid="`item-retailer-${index}`"
                  type="text"
                  class="w-full input"
                  :placeholder="t('ORDERS.PLACEHOLDERS.RETAILER_ID')"
                />
              </label>
              <label>
                <span class="block mb-1 text-xs">{{ t('ORDERS.NAME') }}</span>
                <input
                  v-model="item.name"
                  :data-testid="`item-name-${index}`"
                  type="text"
                  class="w-full input"
                  :class="{
                    'border-red-500': itemFieldHasError(index, 'name'),
                  }"
                  :placeholder="t('ORDERS.PLACEHOLDERS.NAME')"
                />
              </label>
              <label>
                <span class="block mb-1 text-xs">{{
                  t('ORDERS.AMOUNT_CENTS')
                }}</span>
                <input
                  v-model="item.amount"
                  :data-testid="`item-amount-${index}`"
                  type="number"
                  class="w-full input"
                  :class="{
                    'border-red-500': itemFieldHasError(index, 'amount'),
                  }"
                  min="1"
                />
              </label>
              <label>
                <span class="block mb-1 text-xs">{{
                  t('ORDERS.QUANTITY')
                }}</span>
                <input
                  v-model="item.quantity"
                  :data-testid="`item-quantity-${index}`"
                  type="number"
                  class="w-full input"
                  :class="{
                    'border-red-500': itemFieldHasError(index, 'quantity'),
                  }"
                  min="1"
                />
              </label>
            </div>
          </div>
          <button type="button" class="text-sm text-primary" @click="addItem">
            {{ t('ORDERS.ADD_ITEM') }}
          </button>
        </div>

        <div v-if="includeItems" class="grid grid-cols-1 gap-4 md:grid-cols-3">
          <label>
            <span class="block mb-1 text-sm font-medium">{{
              t('ORDERS.TAX_CENTS')
            }}</span>
            <input
              v-model="tax"
              data-testid="tax-input"
              type="number"
              class="w-full input"
              :class="{ 'border-red-500': v$.tax.$error }"
              min="0"
            />
          </label>
          <label>
            <span class="block mb-1 text-sm font-medium">{{
              t('ORDERS.SHIPPING_CENTS')
            }}</span>
            <input
              v-model="shipping"
              data-testid="shipping-input"
              type="number"
              class="w-full input"
              :class="{ 'border-red-500': v$.shipping.$error }"
              min="0"
            />
          </label>
          <label>
            <span class="block mb-1 text-sm font-medium">{{
              t('ORDERS.DISCOUNT_CENTS')
            }}</span>
            <input
              v-model="discount"
              data-testid="discount-input"
              type="number"
              class="w-full input"
              :class="{ 'border-red-500': v$.discount.$error }"
              min="0"
            />
          </label>
        </div>

        <div class="p-4 bg-n-slate-1 rounded-md text-right">
          <p v-if="includeItems" class="text-sm">
            {{ t('ORDERS.SUBTOTAL', { amount: subtotal }) }}
          </p>
          <p class="font-bold">
            {{ t('ORDERS.TOTAL_AMOUNT', { amount: totalAmount }) }}
          </p>
        </div>

        <div class="flex justify-end gap-2 mt-4">
          <NextButton ghost type="button" @click.prevent="onClose">
            {{ t('ORDERS.CANCEL') }}
          </NextButton>
          <NextButton type="submit" :is-loading="isSubmitting" color="blue">
            {{ t('ORDERS.SEND_ORDER') }}
          </NextButton>
        </div>
      </form>
    </div>
  </woot-modal>
</template>
